import { useEffect, useRef, useCallback } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'textarea:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Hook that traps keyboard focus inside a modal dialog.
 *
 * Features (WCAG 2.4.3 / 2.1.2):
 *  - Tab / Shift+Tab cycles through focusable elements inside the container
 *  - Auto-focuses the first focusable element (or the container itself) on open
 *  - Restores focus to the previously-focused element on close
 *  - Closes the modal on Escape key press
 *
 * @param {boolean} isOpen  – whether the modal is currently visible
 * @param {Function} onClose – callback to close the modal (called on Escape)
 * @returns {React.RefObject} ref to attach to the modal's root container element
 */
export function useFocusTrap(isOpen, onClose) {
  const containerRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  // Stable reference to onClose to avoid re-running effects
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // --- Auto-focus on open & restore focus on close ---
  useEffect(() => {
    if (isOpen) {
      // Remember what was focused before the modal opened
      previouslyFocusedRef.current = document.activeElement;

      // Small delay so the DOM/animation can settle
      const timer = setTimeout(() => {
        if (!containerRef.current) return;

        const firstFocusable = containerRef.current.querySelector(FOCUSABLE_SELECTOR);
        if (firstFocusable) {
          firstFocusable.focus();
        } else {
          // Fallback: make the container itself focusable
          containerRef.current.setAttribute('tabindex', '-1');
          containerRef.current.focus();
        }
      }, 50);

      return () => clearTimeout(timer);
    } else {
      // Restore focus when the modal closes
      if (previouslyFocusedRef.current && typeof previouslyFocusedRef.current.focus === 'function') {
        previouslyFocusedRef.current.focus();
        previouslyFocusedRef.current = null;
      }
    }
  }, [isOpen]);

  // --- Trap Tab key & handle Escape ---
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current?.();
        return;
      }

      if (e.key !== 'Tab') return;
      if (!containerRef.current) return;

      const focusableEls = Array.from(
        containerRef.current.querySelectorAll(FOCUSABLE_SELECTOR)
      );

      if (focusableEls.length === 0) {
        e.preventDefault();
        return;
      }

      const firstEl = focusableEls[0];
      const lastEl = focusableEls[focusableEls.length - 1];

      if (e.shiftKey) {
        // Shift+Tab: wrap from first → last
        if (document.activeElement === firstEl || !containerRef.current.contains(document.activeElement)) {
          e.preventDefault();
          lastEl.focus();
        }
      } else {
        // Tab: wrap from last → first
        if (document.activeElement === lastEl || !containerRef.current.contains(document.activeElement)) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen]);

  return containerRef;
}
